import { Button, CircularProgress, TextField, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const CreatePasswordCard = ({
  email,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  error,
  loading,
  handleSetPassword,
  isForgotFlow,
}) => {
  const [validations, setValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    same: false,
  });

  const [showValidations, setShowValidations] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Update validations whenever password changes
    setValidations({
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
			same: password.length > 0 && confirmPassword.length > 0 && password === confirmPassword,
    });
  }, [password, confirmPassword]);

  const getValidationColor = (isValid) => {
    if (!showValidations && !password) return '#5e6c84'; // Default color
    return isValid ? '#36B37E' : '#FF5630'; // Green when valid, red when invalid
  };

  const handleConfirmPasswordFocus = () => {
    setShowValidations(true);
  };

  return (
    <Wrapper className="card-wrapper">
      <div className="card-wrapper-box">
        <h4>{isForgotFlow ? 'Reset Your Password' : 'Set Your Password'}</h4>
        <p className="sub-text">
          {isForgotFlow ? (
            <>
              Create a new password for your account: <b>{email}</b>
            </>
          ) : (
            <>
              Create a password for your account: <b>{email}</b>
            </>
          )}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSetPassword();
          }}
        >
          <div className="input-box">
            <TextField
              fullWidth
              // error={error}
              type={showPassword ? 'text' : 'password'}
              label="Password"
              placeholder="Enter password"
              id="create-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      id="create-password-visibility-toggle"
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="input-box">
            <TextField
              fullWidth
              // error={error}
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm Password"
              placeholder="Confirm password"
              id="create-confirm-password-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={handleConfirmPasswordFocus}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      id="create-confirm-password-visibility-toggle"
                      aria-label={
                        showConfirmPassword
                          ? 'Hide confirm password'
                          : 'Show confirm password'
                      }
                      onClick={() => setShowConfirmPassword((show) => !show)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="password-requirements">
            <p>Password must contain:</p>
            <ul>
              <li style={{ color: getValidationColor(validations.length) }}>
                At least 12 characters
              </li>
              <li style={{ color: getValidationColor(validations.uppercase) }}>
                At least one uppercase letter
              </li>
              <li style={{ color: getValidationColor(validations.lowercase) }}>
                At least one lowercase letter
              </li>
              <li style={{ color: getValidationColor(validations.number) }}>
                At least one number
              </li>
              <li style={{ color: getValidationColor(validations.special) }}>
                At least one special character
              </li>
              <li style={{ color: getValidationColor(validations.same) }}>
                Both passwords must match
              </li>
            </ul>
          </div>

          <Button
            fullWidth
            variant="contained"
            type="submit"
            id="create-password-button"
            disabled={
              loading ||
              !validations.length ||
              !validations.uppercase ||
              !validations.lowercase ||
              !validations.number ||
				!validations.special ||
				!validations.same ||
				!password ||
				!confirmPassword
            }
          >
            {loading ? (
              <CircularProgress style={{ color: '#fff' }} size={26} />
            ) : (
              isForgotFlow ? 'Reset Password' : 'Set Password'
            )}
          </Button>
        </form>
      </div>

      <div className="sign-up-link">
        <p>
          Already have an account? <Link id="create-password-login-link" to="/login">Login</Link>
        </p>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .password-requirements {
    margin: 20px 0;
    padding: 15px;
    background-color: #f4f5f7;
    border-radius: 8px;
  }

  .password-requirements p {
    font-size: 14px;
    color: #5e6c84;
    margin-bottom: 8px;
  }

  .password-requirements ul {
    list-style: none;
    padding-left: 15px;
  }

  .password-requirements li {
    font-size: 12px;
    margin-bottom: 4px;
    position: relative;
    transition: color 0.3s ease;
  }

  .password-requirements li:before {
    content: '•';
    position: absolute;
    left: -15px;
  }

  /* Disable browser's built-in password toggle */
  input[type='password']::-ms-reveal,
  input[type='password']::-webkit-credentials-auto-fill-button {
    display: none !important;
  }

  /* For Firefox */
  input[type='password']::-moz-password-eye {
    display: none !important;
  }
`;

export default CreatePasswordCard;
